'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { FiltersSection, OrdersTable, EditOrderModal, OrderActionModal } from './components';
import { OrderActionType, OrderData, OrdersFilters } from './types';
import { transformCplOrderToOrderData } from './utils';
import {
  DEFAULT_FILTERS,
  DEFAULT_PAGE_SIZE,
  MODELS_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
  MODEL_SEARCH_DEBOUNCE_DELAY,
} from './constants';
import clientCplService, { CplRequestParams } from '../../../api/client/clientCplService';
import clientAccService from '../../../api/client/clientAccService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { showCreatorSuccess, showError } from '../../../utils';

const Orders: React.FC = () => {
  const { t } = useTranslations();
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<OrdersFilters>(DEFAULT_FILTERS);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [availableModels, setAvailableModels] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState<OrderActionType | null>(null);
  const [selectedActionOrder, setSelectedActionOrder] = useState<OrderData | null>(null);
  const [isActionSubmitting, setIsActionSubmitting] = useState(false);

  // Local states for debounced inputs
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [localSubsForm, setLocalSubsForm] = useState(filters.subs_form?.toString() || '');
  const [localIncome, setLocalIncome] = useState(filters.income?.toString() || '');
  const breadcrumbs = useMemo(
    () => [
      { label: t('proposals.home'), href: '/' },
      { label: t('sidebar.orders'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  const loadModels = useCallback(async (searchTerm: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: MODELS_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });

      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const models = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableModels(models);
      } else {
        setAvailableModels([]);
      }
    } catch (error) {
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadModels(modelSearchTerm);
    }, MODEL_SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [modelSearchTerm, loadModels]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  // Debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Debounce for subs_form input
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localSubsForm ? parseInt(localSubsForm) : null;
      setFilters((prev) => ({ ...prev, subs_form: value }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSubsForm]);

  // Debounce for income input
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localIncome ? parseFloat(localIncome) : null;
      setFilters((prev) => ({ ...prev, income: value }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localIncome]);

  // Sync local states with filters when they change externally
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setLocalSubsForm(filters.subs_form?.toString() || '');
  }, [filters.subs_form]);

  useEffect(() => {
    setLocalIncome(filters.income?.toString() || '');
  }, [filters.income]);

  // Мемоизируем параметры API чтобы избежать лишних перерендеров
  const apiParams = useMemo(() => {
    const params: CplRequestParams = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.status !== 'all') {
      // Преобразуем статус для API
      const apiStatus = filters.status === 'pending' ? 'waiting' : filters.status;
      params.status = apiStatus as any;
    }

    if (filters.source !== 'all') {
      params.source = filters.source as any;
    }

    if (filters.campaignType !== 'all') {
      params.model_type = filters.campaignType === 'free' ? 'free' : 'free trial';
    }

    if (filters.models.length > 0) {
      params.models = filters.models;
    }

    if (filters.subs_form !== null) {
      params.subs_form = filters.subs_form;
    }

    if (filters.income !== null) {
      params.income = filters.income;
    }

    if (filters.sort !== 'all') {
      params.sort = filters.sort;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    return params;
  }, [
    currentPage,
    pageSize,
    filters.status,
    filters.source,
    filters.campaignType,
    filters.models,
    filters.subs_form,
    filters.income,
    filters.sort,
    filters.search,
  ]);

  // Load orders when dependencies change
  useEffect(() => {
    const loadOrders = async () => {
      setIsLoading(true);
      try {
        const response: any = await clientCplService.getOrders(apiParams);

        // Новая структура API: данные приходят напрямую без дополнительной обертки
        if (response && response.objects && Array.isArray(response.objects)) {
          const transformedOrders = response.objects.map(transformCplOrderToOrderData);

          setOrders(transformedOrders);
          setTotalCount(response.count || 0);
        } else {
          setOrders([]);
          setTotalCount(0);
        }
      } catch (error) {
        setOrders([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrders();
  }, [apiParams]);

  const filteredOrders = useMemo(() => {
    let result = orders;

    // if (filters.search) {
    //   result = result.filter(order => {
    //     const searchLower = filters.search.toLowerCase();
    //     return order.internalId.toLowerCase().includes(searchLower) ||
    //            order.account.name.toLowerCase().includes(searchLower) ||
    //            order.account.username.toLowerCase().includes(searchLower) ||
    //            order.offer.name.toLowerCase().includes(searchLower) ||
    //            order.campaign.id.toLowerCase().includes(searchLower);
    //   });
    // }

    // if (filters.sort !== 'all') {
    //   result = [...result].sort((a, b) => {
    //     const isDesc = filters.sort.startsWith('-');
    //     const sortKey = isDesc ? filters.sort.substring(1) : filters.sort;
    //     const multiplier = isDesc ? -1 : 1;

    //     switch (sortKey) {
    //       case 'created_at':
    //         return multiplier * (new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    //       case 'arpu':
    //         return multiplier * (a.campaign.statistics.arpu - b.campaign.statistics.arpu);
    //       case 'romi':
    //         return multiplier * (a.campaign.statistics.romi - b.campaign.statistics.romi);
    //       case 'subscribed_count':
    //         return multiplier * (a.offer.subscribers - b.offer.subscribers);
    //       case 'transactions_statistic.total':
    //         return multiplier * (a.campaign.statistics.transactions - b.campaign.statistics.transactions);
    //       case 'transactions_statistic.1':
    //         return multiplier * (a.campaign.statistics.transactions - b.campaign.statistics.transactions);
    //       case 'subs.with_gte_3_chat_messages_count':
    //         // @ts-ignore
    //         return multiplier * (a.campaign.statistics.messages - b.campaign.statistics.messages);
    //       default:
    //         return 0;
    //     }
    //   });
    // }

    return result;
  }, [orders]);

  const handleFilterChange = useCallback(
    (key: keyof OrdersFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleEditOrder = useCallback((order: OrderData) => {
    setSelectedOrder(order);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedOrder(null);
  }, []);

  const handleSaveOrder = useCallback((orderData: any) => {}, []);

  const getApiErrorMessage = useCallback(
    (error: any, fallbackMessage: string) => {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstDetail = detail[0];
        return firstDetail?.msg || fallbackMessage;
      }
      if (typeof detail === 'string') {
        return detail;
      }
      return error?.message || fallbackMessage;
    },
    []
  );

  const handleOrderAction = useCallback(
    (order: OrderData, actionType: OrderActionType) => {
      const isOfferActive = order.campaign.status === 'access_active';
      if (!isOfferActive) {
        showError(
          actionType === 'continue'
            ? t('orders.actions.continueInactiveOffer')
            : t('orders.actions.repeatInactiveOffer')
        );
        return;
      }

      setSelectedActionOrder(order);
      setSelectedActionType(actionType);
      setIsActionModalOpen(true);
    },
    [t]
  );

  const handleCloseActionModal = useCallback(() => {
    if (isActionSubmitting) return;
    setIsActionModalOpen(false);
    setSelectedActionOrder(null);
    setSelectedActionType(null);
  }, [isActionSubmitting]);

  const buildOrderDataLikeCreate = useCallback((order: OrderData, subscribers: number) => {
    const rawOrder = order as any;
    // Keep payload format identical to create-order flow:
    // { subs, model, type, offer, access_work_media }
    const mappedCampaignType =
      rawOrder?.model?.type === 'trial'
        ? 'free trial'
        : rawOrder?.model?.type || (order.campaign.type === 'free' ? 'free' : 'free trial');

    const existingWorkMedia = rawOrder?.access_work_media || {};
    const workMedia = {
      nonude: existingWorkMedia?.nonude ?? true,
      tt: existingWorkMedia?.tt ?? false,
      lingerie: existingWorkMedia?.lingerie ?? false,
      lite_nude: existingWorkMedia?.lite_nude ?? false,
      full_nude: existingWorkMedia?.full_nude ?? false,
    };

    return {
      subs: {
        count: subscribers,
      },
      model: {
        id: rawOrder?.model?.id || order.account.id,
        type: mappedCampaignType,
      },
      type: mappedCampaignType,
      offer: order.offer.id,
      access_work_media: workMedia,
    };
  }, []);

  const handleSubmitOrderAction = useCallback(
    async (subscribers: number) => {
      if (!selectedActionOrder || !selectedActionType) return;

      setIsActionSubmitting(true);
      try {
        const orderPayload = buildOrderDataLikeCreate(selectedActionOrder, subscribers);

        if (selectedActionType === 'continue') {
          await clientCplService.proceedOrder(selectedActionOrder.id, orderPayload);
          showCreatorSuccess(t('orders.actions.continueSuccess'));
        } else {
          await clientCplService.repeatOrder(selectedActionOrder.id, orderPayload);
          showCreatorSuccess(t('orders.actions.repeatSuccess'));
        }
        handleCloseActionModal();
        const response: any = await clientCplService.getOrders(apiParams);
        if (response?.objects && Array.isArray(response.objects)) {
          const transformedOrders = response.objects.map(transformCplOrderToOrderData);
          setOrders(transformedOrders);
          setTotalCount(response.count || 0);
        }
      } catch (error: any) {
        const fallbackMessage =
          selectedActionType === 'continue'
            ? t('orders.actions.continueError')
            : t('orders.actions.repeatError');
        showError(getApiErrorMessage(error, fallbackMessage));
      } finally {
        setIsActionSubmitting(false);
      }
    },
    [
      selectedActionOrder,
      selectedActionType,
      t,
      handleCloseActionModal,
      buildOrderDataLikeCreate,
      apiParams,
      getApiErrorMessage,
    ]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setLocalSubsForm('');
    setLocalIncome('');
    setCurrentPage(1);
  }, []);

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.sort !== 'all' ||
      filters.models.length > 0 ||
      filters.campaignType !== 'all' ||
      filters.status !== 'all' ||
      filters.source !== 'all' ||
      filters.subs_form !== null ||
      filters.income !== null
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('orders.title')}
        </Typography>
      </div>

      <FiltersSection
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        localSearch={localSearch}
        localSubsForm={localSubsForm}
        localIncome={localIncome}
        availableModels={availableModels}
        modelsLoading={modelsLoading}
        sortDirection={sortDirection}
        onSearchChange={setLocalSearch}
        onSubsFormChange={setLocalSubsForm}
        onIncomeChange={setLocalIncome}
        onFilterChange={handleFilterChange}
        onModelSearchChange={setModelSearchTerm}
        onSortDirectionChange={setSortDirection}
        onResetFilters={handleResetFilters}
      />

      {/* Таблица */}
      <div className="px-4 md:px-6 md:h-full">
        <OrdersTable
          orders={filteredOrders}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onEditOrder={handleEditOrder}
          onOrderAction={handleOrderAction}
        />
      </div>

      {/* Edit Order Modal */}
      <EditOrderModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        order={selectedOrder}
        onSave={handleSaveOrder}
      />
      <OrderActionModal
        isOpen={isActionModalOpen}
        onClose={handleCloseActionModal}
        order={selectedActionOrder}
        actionType={selectedActionType}
        onSubmit={handleSubmitOrderAction}
        isSubmitting={isActionSubmitting}
      />
    </div>
  );
};

export default Orders;
